import { Button, Col, Form, FormInstance, Row, Select } from "antd";
import CaretDownIcon from "../../../../assets/icons/CaretDownIcon";
import CustomDrawer from "../../../../components/common/CustomDrawer";
import { useGetAllDepartmentQuery } from "../../../../features/superAdmin/employeeService/employeeManagementService";
import { useGetAllFeaturesQuery } from "../../../../features/superAdmin/ticketService/feature/feature.api";
import { useGetAllProductsQuery } from "../../../../features/superAdmin/ticketService/product/product.api";
import { CustomModalProps } from "../../../../types";
import { TEMPLATE_TYPES } from "../../TicketManagement/constants/ticket.constants";
import { DFilters } from "../Dashboard";

type Props = {
  isFiltered?: boolean;
  handleFilter: <K extends keyof DFilters>(
    filterType: K,
    value: DFilters[K],
  ) => void;
  form: FormInstance<any>;
} & CustomModalProps;

const FilterDrawer = ({
  handleFilter,
  open,
  handleClose,
  isFiltered,
  form,
}: Props) => {
  // ------ Watched values ------
  const selectedProduct = Form.useWatch("product", form);
  const selectedTemplateType = Form.useWatch("template_type", form);

  // ------ Function for form finish ------
  const handleFinish = (values: any) => {
    const { product, feature, template_type, severity, priority, department } =
      values || {};
    handleFilter("department_id", department);
    handleFilter("product_id", product);
    handleFilter("feature_id", feature);
    handleFilter("template_type", template_type);
    handleFilter("severity", severity);
    handleFilter("priority", priority);
    handleClose();
  };

  // ------ Function for clear filter ------
  const handleFilterClear = () => {
    handleFilter("department_id", undefined);
    handleFilter("product_id", undefined);
    handleFilter("feature_id", undefined);
    handleFilter("template_type", undefined);
    handleFilter("severity", undefined);
    handleFilter("priority", undefined);
    form.resetFields();
    // handleClose();
  };
  // ------ Queries ------
  const { data: productData, isLoading: isProductLoading } =
    useGetAllProductsQuery({
      params: {
        page: 1,
        limit: 100000,
        status: "active",
      },
    });
  const {
    data: featureData,
    isLoading: isFeatureLoading,
    isFetching: isFeatureFetching,
  } = useGetAllFeaturesQuery(
    {
      params: {
        page: 1,
        limit: 100000,
        status: "active",
        product_id: selectedProduct,
      },
    },
    {
      skip: !selectedProduct,
    },
  );
  const { data: departmentsData } = useGetAllDepartmentQuery({
    params: {
      page: 1,
      limit: 100000000,
      status: "active",
    },
  });

  // ------ Mapped Select Options ------
  const productOptions = productData?.data?.products?.map((p) => ({
    value: p.id,
    label: p.title,
  }));
  const featureOptions = featureData?.data?.features?.map((p) => ({
    value: p.id,
    label: p.title,
  }));
  const departmentOptions = departmentsData?.data.map((dep: any) => ({
    label: dep.name,
    value: dep.id,
  }));

  return (
    <CustomDrawer
      open={open}
      onClose={() => {
        handleClose();
        !isFiltered && form.resetFields();
      }}
      title="Filter"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        className="space-y-5"
      >
        <Form.Item name="department">
          <Select
            allowClear
            size="large"
            suffixIcon={<CaretDownIcon />}
            placeholder="Department"
            options={departmentOptions}
          />
        </Form.Item>
        <Form.Item name="product">
          <Select
            allowClear
            size="large"
            suffixIcon={<CaretDownIcon />}
            placeholder="Product"
            options={productOptions}
            disabled={isProductLoading}
            onChange={() => form.resetFields(["feature"])}
          />
        </Form.Item>
        <Form.Item name="feature">
          <Select
            allowClear
            size="large"
            suffixIcon={<CaretDownIcon />}
            placeholder="Features"
            options={featureOptions}
            disabled={
              !selectedProduct ||
              isProductLoading ||
              isFeatureFetching ||
              isFeatureLoading
            }
          />
        </Form.Item>

        <Form.Item name="template_type">
          <Select
            allowClear
            size="large"
            placeholder="Template Type"
            suffixIcon={<CaretDownIcon />}
            className=""
            onChange={() => form.resetFields(["severity"])}
            options={[
              {
                label: TEMPLATE_TYPES.workflow,
                value: TEMPLATE_TYPES.workflow,
              },
              {
                label: TEMPLATE_TYPES.sla,
                value: TEMPLATE_TYPES.sla,
              },
            ]}
          />
        </Form.Item>

        <Form.Item name="priority">
          <Select
            allowClear
            size="large"
            suffixIcon={<CaretDownIcon />}
            placeholder="Priority"
            options={priorities}
          />
        </Form.Item>

        {selectedTemplateType === TEMPLATE_TYPES.sla ? (
          <Form.Item name="severity">
            <Select
              allowClear
              size="large"
              suffixIcon={<CaretDownIcon />}
              placeholder="Severity"
              options={severities}
            />
          </Form.Item>
        ) : null}

        <Row gutter={12}>
          <Col span={8}>
            <Button
              className="button-bordered-light h-10 !w-full"
              onClick={() => {
                handleClose();
                !isFiltered && form.resetFields();
              }}
            >
              Cancel
            </Button>
          </Col>
          {isFiltered && (
            <Col span={8}>
              <Button
                className="button-primary-light h-10 !w-full"
                onClick={() => handleFilterClear()}
              >
                Clear
              </Button>
            </Col>
          )}
          <Col span={8}>
            <Button
              className="button-primary h-10 !w-full"
              type="primary"
              htmlType="submit"
            >
              Filter
            </Button>
          </Col>
        </Row>
      </Form>
    </CustomDrawer>
  );
};

export default FilterDrawer;

const priorities = [
  {
    value: "critical",
    label: "P1",
  },
  {
    value: "major",
    label: "P2",
  },
  {
    value: "moderate",
    label: "P3",
  },
  {
    value: "minor",
    label: "P4",
  },
];
const severities = [
  {
    value: "s1",
    label: "S1",
  },
  {
    value: "s2",
    label: "S2",
  },
  {
    value: "s3",
    label: "S3",
  },
  {
    value: "s4",
    label: "S4",
  },
];
